/**
 * Directions URL helper.
 *
 * Single source of truth for the "open in maps" URL used by the
 * Find-a-charger list rows. Coordinates are the canonical
 * destination — a name is useful for context but never a substitute
 * for lat/lng.
 *
 * Validation is deliberately strict: only render "Get directions"
 * when the caller passes a real, in-range coordinate pair — that
 * way we never surface a link that points at (0, 0) in the Atlantic
 * because a legacy DB row defaulted its coords to zero, and we
 * never generate a URL from stale UI state (map centre, geolocation,
 * a different charger's coords, or a Barbados fallback).
 *
 * On mobile, https://www.google.com/maps/dir/?api=1 links are
 * handed off to the installed Google Maps or Apple Maps app by the
 * OS.
 */

const GOOGLE_MAPS_DIRECTIONS_BASE = 'https://www.google.com/maps/dir/?api=1';

function isFiniteNumber(n) {
  return typeof n === 'number' && Number.isFinite(n);
}

/**
 * Whether a charger object carries valid, plottable coordinates.
 * A coordinate pair is valid when:
 *   - both are finite numbers (no NaN, no Infinity, no strings)
 *   - latitude is in the WGS84 range [-90, 90]
 *   - longitude is in the WGS84 range [-180, 180]
 *   - the pair is not (0, 0), which is used as a "coords unknown"
 *     sentinel by the API and matches the null-island guard in
 *     mapStatus.js so the map and the directions action agree on
 *     what "has coordinates" means.
 */
export function hasDirectionsCoords(charger) {
  const lat = charger?.lat;
  const lng = charger?.lng;
  if (!isFiniteNumber(lat) || !isFiniteNumber(lng)) return false;
  if (lat < -90 || lat > 90) return false;
  if (lng < -180 || lng > 180) return false;
  if (lat === 0 && lng === 0) return false;
  return true;
}

/**
 * Build a Google Maps directions URL, or return null if the charger
 * doesn't have real coordinates. The URL is safely encoded — even
 * though a decimal number never needs encoding, we go through
 * `encodeURIComponent` so a future change to the coord type can't
 * accidentally inject query-string chars.
 *
 * @param {{ lat?: number, lng?: number }} charger
 * @returns {string | null}
 */
export function directionsUrl(charger) {
  if (!hasDirectionsCoords(charger)) return null;
  const destination = `${encodeURIComponent(charger.lat)},${encodeURIComponent(charger.lng)}`;
  return `${GOOGLE_MAPS_DIRECTIONS_BASE}&destination=${destination}`;
}
